/**
 * Pagina dettaglio fattura del modulo documentale.
 *
 * Mostra tutti i dati della fattura incluse righe e allegati.
 * Evidenzia lo stato SDI e mostra l'alert di abbinamento se necessario.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  type ComboboxOption,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@floraltech/ui';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AbbinamentoAlert from '../components/AbbinamentoAlert';
import StatoBadge from '../components/StatoBadge';
import StatoPagamentoBadge from '../components/StatoPagamentoBadge';
import AdeXmlViewer from '../components/AdeXmlViewer';
import PagamentoFatturaModal from '../components/PagamentoFatturaModal';
import IntegrazioneCard from '../components/IntegrazioneCard';
import ModificaAnagraficaFatturaDialog from '../components/ModificaAnagraficaFatturaDialog';
import RevocaBozzaDialog from '../components/RevocaBozzaDialog';

/** Stati in cui la fattura attiva risulta emessa: incassabile, non piu' correggibile
 *  con un reinvio. Deve restare allineato a stati_fattura.STATI_EMESSA_VALIDA. */
const STATI_EMESSA_VALIDA = ['consegnata', 'non_consegnata', 'trasmessa', 'accettata'];
/** Tipi di documento che INTEGRANO una fattura di acquisto in inversione
 *  contabile, invece di stornarla. Condividono con la nota di credito il campo
 *  che punta al documento collegato, quindi senza questa distinzione
 *  l'etichetta del collegamento direbbe il falso su meta' dei casi. */
const TIPI_INTEGRAZIONE = ['TD16', 'TD17', 'TD18', 'TD19'];

/** Stati da cui si puo' tornare in bozza: la fattura non e' mai stata emessa. */
const STATI_RECUPERABILI = ['scartata', 'rifiutata'];

interface PagamentoRiga {
  id: string;
  ritenuta_operata?: number | string | null;
  data_pagamento: string;
  importo: number | string;
  modalita_codice: string | null;
  tipo_riferimento: string;
  riferimento: string | null;
  centro_costo_id: string | null;
  note: string | null;
}

interface Riga {
  id: string;
  numero_linea: number;
  descrizione: string | null;
  quantita: number | null;
  unita_misura: string | null;
  prezzo_unitario: number | null;
  aliquota_iva_percentuale: number | null;
  natura_iva_codice: string | null;
  importo_riga: number | null;
}

interface Allegato {
  id: string;
  nome_file: string;
  tipo_mime: string | null;
  dimensione: number | null;
}

interface FatturaDetail {
  id: string;
  year: number;
  numero: string | null;
  data: string;
  tipo_documento_codice: string;
  direzione: string;
  stato: string;
  cedente_denominazione: string | null;
  cedente_piva: string | null;
  cedente_cf: string | null;
  cessionario_denominazione: string | null;
  cessionario_piva: string | null;
  cessionario_cf: string | null;
  cessionario_codice_sdi: string | null;
  importo_imponibile: number | null;
  importo_iva: number | null;
  importo_totale: number | null;
  stato_pagamento: string | null;
  data_scadenza: string | null;
  condizioni_pagamento: string | null;
  modalita_pagamento: string | null;
  iban_pagamento: string | null;
  importo_pagato: number | null;
  importo_ritenuta: number | null;
  importo_da_incassare: number | null;
  tipo_ritenuta: string | null;
  aliquota_ritenuta: number | null;
  importo_bollo: number | null;
  fattura_riferimento_id: string | null;
  has_xml: boolean;
  abbinamento_stato: string;
  stato_sdi: string | null;
  id_invoicetronic: string | null;
  sdi_descrizione: string | null;
  sdi_errori: string[] | null;
  data_ricezione_sdi: string | null;
  origine_tipo: string | null;
  origine_id: string | null;
  preventivo_id: string | null;
  ddt_id: string | null;
  documento_id: string | null;
  centro_costo_id: string | null;
  acconti: AccontoRef[];
  righe: Riga[];
  allegati: Allegato[];
}

interface AccontoRef {
  id: string;
  year: number;
  numero: string | null;
  importo_totale: number | null;
}

interface Centro {
  id: string;
  codice: string;
  nome: string;
}

const fmtEUR = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
const fmtEUR4 = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 4, maximumFractionDigits: 4 });

/** Formatta byte in unità leggibile (KB, MB). */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Pagina dettaglio fattura con righe, allegati e stato SDI.
 */
const FatturaDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const year = searchParams.get('year') ?? String(new Date().getFullYear());

  const [fattura, setFattura] = useState<FatturaDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  // QUALE azione e' in corso. `actionLoading` da solo disabilita tutti i
  // bottoni ed e' giusto cosi' (due operazioni SDI insieme non si vogliono),
  // ma veniva usato anche per le ETICHETTE: premendo "Scarica PDF" anche
  // "Verifica XML" scriveva "Verifica...". L'interfaccia dichiarava il falso.
  const [azione, setAzione] = useState<string | null>(null);
  const [showPagamentoModal, setShowPagamentoModal] = useState(false);
  const [showModificaDialog, setShowModificaDialog] = useState(false);
  const [showRevocaDialog, setShowRevocaDialog] = useState(false);
  // Valorizzato solo quando si corregge un pagamento esistente; null = nuovo.
  const [pagamentoDaCorreggere, setPagamentoDaCorreggere] =
    useState<PagamentoRiga | null>(null);
  const [pagamenti, setPagamenti] = useState<PagamentoRiga[]>([]);
  // Distinguono "nessun pagamento registrato" da "non ho potuto leggerli".
  // Senza questa distinzione una chiamata fallita e una fattura mai incassata
  // sono identiche a schermo, e su una fattura gia' pagata si finisce per
  // registrare il pagamento una seconda volta.
  const [pagamentiErrore, setPagamentiErrore] = useState<string | null>(null);
  const [pagamentiLoading, setPagamentiLoading] = useState(false);
  const [centri, setCentri] = useState<Centro[]>([]);
  // Riscontro dell'assegnazione del centro di costo: senza, l'azione e' muta.
  const [centroEsito, setCentroEsito] = useState<{ ok: boolean; testo: string } | null>(null);
  const [centroSalvataggio, setCentroSalvataggio] = useState(false);
  // Testo digitato nel campo del centro di costo: il Combobox non filtra da
  // solo, riceve le opzioni gia' scremate (vedi centriOptions).
  const [centroFiltro, setCentroFiltro] = useState('');
  const [verificaXml, setVerificaXml] = useState<{
    xml: string;
    valido: boolean;
    errori_xsd: string[];
    errori_sdi: string[];
    sdi_eseguito: boolean;
  } | null>(null);
  const [showVerifica, setShowVerifica] = useState(false);
  const [pdfErrore, setPdfErrore] = useState<string | null>(null);

  const auth = () => ({
    Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  });

  const handleVerificaXml = async (conSdi = false) => {
    if (!id) return;
    if (conSdi) {
      const conferma = window.confirm(
        'Eseguire il controllo SDI su questa fattura?\n\n' +
          'Il controllo applica le regole reali del Sistema di Interscambio ' +
          'SENZA inviare la fattura, ma CONSUMA 1 credito Invoicetronic (lo ' +
          'stesso contatore degli invii).\n\n' +
          'La verifica locale (XSD) resta sempre gratuita.'
      );
      if (!conferma) return;
    }
    setActionLoading(true);
    setAzione('verifica');
    setError(null);
    try {
      const res = await fetch(
        `/api/v1/fatture/${id}/verifica-xml?year=${year}&con_sdi=${conSdi}`,
        { headers: auth() }
      );
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        throw new Error(b?.detail ?? `Errore ${res.status}`);
      }
      setVerificaXml(await res.json());
      setShowVerifica(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore verifica XML');
    } finally {
      setActionLoading(false);
      setAzione(null);
    }
  };

  const handleInviaSdi = async () => {
    if (!id || !fattura) return;
    const conferma = window.confirm(
      `Inviare la fattura ${fattura.numero ?? ''} a SDI?\n\n` +
        "E' un'operazione fiscale reale e NON reversibile. Assicurati di aver fatto la Verifica XML."
    );
    if (!conferma) return;
    setActionLoading(true);
    setAzione('invio');
    setError(null);
    try {
      const res = await fetch(`/api/v1/fatture/${id}/invia-sdi?year=${year}`, {
        method: 'POST',
        headers: auth(),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        if (res.status === 422 && body?.detail?.errori_validazione) {
          throw new Error(
            'Validazione fallita, NON inviata:\n- ' +
              body.detail.errori_validazione.join('\n- ')
          );
        }
        throw new Error(
          typeof body?.detail === 'string' ? body.detail : `Errore ${res.status}`
        );
      }
      await caricaFattura();
      // Era un window.alert bloccante su un ESITO POSITIVO: un click regalato a
      // ogni invio. La conferma preventiva resta (l'invio e' irreversibile e
      // consuma un credito), l'esito no.
      toast.success(`Fattura ${fattura.numero ?? ''} inviata a SDI.`, {
        description:
          `Stato: ${body?.stato ?? 'in_invio'} - ` +
          `ID Invoicetronic: ${body?.id_invoicetronic ?? '-'}`,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore invio SDI');
    } finally {
      setActionLoading(false);
      setAzione(null);
    }
  };

  const handleRiportaInBozza = async () => {
    if (!id || !fattura) return;
    const conferma = window.confirm(
      `La fattura ${fattura.numero ?? ''} e' stata SCARTATA da SDI.\n\n` +
        "Riportarla in bozza per correggerla e reinviarla? Il numero resta lo stesso " +
        "(una fattura scartata e' come mai emessa)."
    );
    if (!conferma) return;
    setActionLoading(true);
    setAzione('bozza');
    setError(null);
    try {
      const res = await fetch(`/api/v1/fatture/${id}/riporta-in-bozza?year=${year}`, {
        method: 'POST',
        headers: auth(),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        throw new Error(
          typeof b?.detail === 'string' ? b.detail : `Errore ${res.status}`
        );
      }
      await caricaFattura();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore riporta in bozza');
    } finally {
      setActionLoading(false);
      setAzione(null);
    }
  };

  const handleAggiornaStatoSdi = async () => {
    if (!id) return;
    setActionLoading(true);
    setAzione('aggiorna');
    setError(null);
    try {
      const res = await fetch(`/api/v1/fatture/${id}/aggiorna-stato-sdi?year=${year}`, {
        method: 'POST',
        headers: auth(),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(
          typeof body?.detail === 'string' ? body.detail : `Errore ${res.status}`
        );
      }
      await caricaFattura();
      const errs = (body?.errori ?? []) as string[];
      const nonRiconosciuto = body?.riconosciuto === false;
      window.alert(
        (nonRiconosciuto
          ? `ATTENZIONE: lo SDI ha risposto "${body?.stato_sdi ?? '-'}", uno stato che ` +
            "l'ERP non sa ancora interpretare. La fattura resta 'in invio': segnala " +
            'questo stato allo sviluppatore.\n\n'
          : '') +
          `Stato aggiornato da SDI: ${body?.stato ?? '-'} (SDI: ${body?.stato_sdi ?? '-'}).` +
          (body?.descrizione ? `\n${body.descrizione}` : '') +
          (errs.length ? `\n\nErrori SDI:\n- ${errs.join('\n- ')}` : '')
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore aggiorna stato SDI');
    } finally {
      setActionLoading(false);
      setAzione(null);
    }
  };

  // Scarica il PDF di cortesia della fattura (funziona su bozza, trasmessa
  // e passiva). L'errore va in pdfErrore (banner inline accanto al bottone,
  // non window.alert/setError: setError sostituirebbe l'intera pagina con
  // il messaggio, facendo perdere il dettaglio all'utente per un problema
  // che riguarda solo il download; un alert bloccante non serve qui).
  const handleScaricaPdf = async () => {
    if (!id) return;
    setActionLoading(true);
    setAzione('pdf');
    setPdfErrore(null);
    try {
      const res = await fetch(`/api/v1/fatture/${id}/pdf?year=${year}`, {
        headers: auth(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(
          typeof body?.detail === 'string'
            ? body.detail
            : `Errore ${res.status} nella generazione del PDF`
        );
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const nomeFattura = fattura?.numero ?? fattura?.id.slice(0, 8) ?? id;
      a.download = `fattura_${nomeFattura.replace('/', '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setPdfErrore(
        err instanceof Error ? err.message : 'Errore nella generazione del PDF'
      );
    } finally {
      setActionLoading(false);
      setAzione(null);
    }
  };

  /**
   * Esegue la revoca. La conferma NON e' piu' qui: la raccoglie
   * RevocaBozzaDialog, che dice la verita' sul caso (con o senza ordine di
   * origine) e pretende che l'utente scriva il numero. Un window.confirm non
   * puo' fare nessuna delle due cose.
   */
  const handleRevoca = async () => {
    if (!id || !fattura) return;
    setActionLoading(true);
    // L'unica azione irreversibile della pagina era anche l'unica senza
    // etichetta di avanzamento: `azione` non veniva mai impostata.
    setAzione('revoca');
    setError(null);
    const numeroRevocato = fattura.numero;
    try {
      const res = await fetch(`/api/v1/fatture/${id}/revoca?year=${year}`, {
        method: 'POST',
        headers: auth(),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        throw new Error(b?.detail ?? `Errore ${res.status}`);
      }
      const data = await res.json();
      setShowRevocaDialog(false);
      toast.success(
        numeroRevocato ? `Bozza n. ${numeroRevocato} revocata.` : 'Bozza revocata.'
      );
      if (data.origine_tipo === 'ordine_vendita' && data.origine_id) {
        navigate(`/ordini-vendita/${data.origine_id}`);
      } else {
        navigate('/documentale/fatture');
      }
    } catch (err) {
      setShowRevocaDialog(false);
      setError(err instanceof Error ? err.message : 'Errore revoca fattura');
    } finally {
      setActionLoading(false);
      setAzione(null);
    }
  };

  useEffect(() => {
    if (!id) return;
    void (async () => {
      // La lista la decide il BACKEND, che applica le stesse regole della
      // validazione: solo voci di dettaglio (mai i raggruppamenti) e solo di
      // natura coerente con la direzione del documento. Prima si chiedevano i
      // primi 200 centri di qualsiasi tipo, e da li' nascevano le imputazioni
      // sbagliate: il contenitore di ramo era offerto come una destinazione.
      const r = await fetch(`/api/v1/fatture/${id}/centri-imputabili?year=${year}`, {
        headers: auth(),
      });
      if (r.ok) {
        const d = await r.json();
        setCentri(Array.isArray(d) ? d : (d.items ?? []));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, year]);

  const centroLabel = (cid: string | null): string => {
    if (!cid) return '-';
    const c = centri.find((x) => x.id === cid);
    return c ? `${c.codice} - ${c.nome}` : '-';
  };

  // Assegnazione centro di costo (analitica): consentita in qualsiasi stato,
  // anche per fatture passive ricevute da SDI.
  /**
   * Assegna il centro di costo e DICHIARA sempre l'esito.
   *
   * Prima era l'unica azione completamente muta della pagina: in caso di
   * errore il controllo tornava al valore precedente senza dire nulla e
   * l'utente restava convinto di avere assegnato il centro. Ora ogni esito,
   * riuscito o fallito, produce un riscontro visibile accanto al campo.
   */
  const assegnaCentro = async (cid: string) => {
    if (!id) return;
    setCentroEsito(null);
    setCentroSalvataggio(true);
    try {
      const res = await fetch(`/api/v1/fatture/${id}/centro-costo?year=${year}`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ centro_costo_id: cid || null }),
      });
      if (!res.ok) {
        setCentroEsito({
          ok: false,
          testo: `Centro di costo NON assegnato (errore ${res.status}). Il valore e' rimasto quello di prima.`,
        });
        return;
      }
      setFattura((prev) => (prev ? { ...prev, centro_costo_id: cid || null } : prev));
      setCentroEsito({
        ok: true,
        testo: cid ? 'Centro di costo assegnato.' : 'Centro di costo rimosso.',
      });
    } catch {
      setCentroEsito({
        ok: false,
        testo: "Centro di costo NON assegnato: controlla la connessione. Il valore e' rimasto quello di prima.",
      });
    } finally {
      setCentroSalvataggio(false);
    }
  };

  const caricaFattura = async () => {
    const response = await fetch(`/api/v1/fatture/${id}?year=${year}`, {
      headers: auth(),
    });
    if (!response.ok) throw new Error(`Errore ${response.status}`);
    setFattura(await response.json());
  };

  /**
   * Carica i pagamenti registrati sulla fattura.
   *
   * NON solleva mai: un errore qui resta confinato alla sua card e non deve
   * far sparire il dettaglio della fattura (BL-054). L'esito si legge in
   * `pagamentiErrore`, che la card traduce in un avviso con "Riprova".
   *
   * In caso di errore l'elenco precedente NON viene svuotato: cancellarlo
   * riprodurrebbe proprio l'ambiguita' che questa funzione esiste per togliere
   * (elenco vuoto indistinguibile da lettura fallita). L'avviso in testa alla
   * card dice che quello a schermo potrebbe non essere aggiornato.
   */
  const caricaPagamenti = async () => {
    setPagamentiLoading(true);
    setPagamentiErrore(null);
    try {
      const r = await fetch(`/api/v1/fatture/${id}/pagamenti?year=${year}`, {
        headers: auth(),
      });
      if (!r.ok) {
        setPagamentiErrore(
          `Non e' stato possibile leggere i pagamenti registrati (errore ${r.status}).`,
        );
        return;
      }
      setPagamenti(await r.json());
    } catch {
      setPagamentiErrore(
        "Non e' stato possibile leggere i pagamenti registrati: controlla la connessione.",
      );
    } finally {
      setPagamentiLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        await caricaFattura();
        await caricaPagamenti();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Errore sconosciuto');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, year]);

  const eliminaPagamento = async (pid: string) => {
    if (!id) return;
    // Il motivo e' obbligatorio lato backend: cancellare un pagamento cambia
    // lo stato fiscale della fattura e la ragione resta agli atti.
    const motivo = window.prompt(
      'Perche stai eliminando questo pagamento? Il motivo resta agli atti.',
    );
    if (motivo === null) return;
    // Qui NON si usa setError: in questa pagina sostituisce l'intero dettaglio
    // della fattura con una riga di testo (BL-054). Un motivo lasciato vuoto
    // non deve far sparire la schermata.
    if (!motivo.trim()) {
      toast.warning('Indica il motivo della cancellazione: resta agli atti.');
      return;
    }
    setActionLoading(true);
    try {
      const res = await fetch(
        `/api/v1/fatture/${id}/pagamenti/${pid}?year=${year}` +
          `&motivo_rettifica=${encodeURIComponent(motivo.trim())}`,
        { method: 'DELETE', headers: auth() },
      );
      if (!res.ok) {
        const d = (await res.json().catch(() => ({}))) as { detail?: unknown };
        toast.error(typeof d.detail === 'string' ? d.detail : `Errore ${res.status}`);
        return;
      }
      // La riga sparisce dall'elenco in memoria invece di rifare la GET dei
      // pagamenti: un refetch completo rimonta la tabella e riporta lo
      // scorrimento in cima, proprio mentre si sta lavorando su una riga.
      // `caricaFattura` resta perche' cancellare un pagamento cambia i totali
      // e lo stato di pagamento del documento, che il DELETE non restituisce.
      setPagamenti((prev) => prev.filter((p) => p.id !== pid));
      await caricaFattura();
      toast.success('Pagamento eliminato.');
    } finally {
      setActionLoading(false);
      setAzione(null);
    }
  };

  if (loading) {
    return (
      <p className="p-6 text-center text-sm text-muted-foreground">
        Caricamento...
      </p>
    );
  }

  // BL-054: prima la condizione era `error || !fattura`, percio' QUALUNQUE
  // errore di una qualsiasi delle azioni della pagina sostituiva l'intero
  // dettaglio della fattura con una riga di testo rosso, e i dati sparivano
  // dagli occhi dell'utente. Ora si esce solo quando la fattura non c'e'
  // davvero; se la fattura c'e', l'errore diventa un banner sopra i dati,
  // che restano leggibili.
  if (!fattura) {
    return (
      <p className="p-6 text-center text-sm text-destructive">
        {error ?? 'Fattura non trovata'}
      </p>
    );
  }

  /**
   * Perche' "Registra pagamento" e' bloccato, oppure null se e' consentito.
   *
   * Unico punto di verita': lo usano sia il bottone in testata sia quello nella
   * card Pagamento, cosi' non possono divergere, e la stessa stringa serve a
   * spiegare all'utente il blocco invece di lasciargli un bottone grigio muto.
   */
  const motivoBloccoPagamento: string | null =
    fattura.stato_pagamento === 'pagata'
      ? "La fattura risulta gia' saldata: per rettificare un importo usa Correggi sul pagamento gia' registrato."
      : fattura.direzione === 'attiva' && !STATI_EMESSA_VALIDA.includes(fattura.stato)
        ? "La fattura attiva non risulta ancora emessa: l'incasso si registra dopo l'esito del Sistema di Interscambio."
        : null;

  /** Etichetta del centro attualmente assegnato, mostrata quando non si sta
   *  digitando un filtro. */
  const centroSelezionatoLabel = (() => {
    if (!fattura.centro_costo_id) return '';
    const c = centri.find((x) => x.id === fattura.centro_costo_id);
    return c ? `${c.codice} - ${c.nome}` : '';
  })();

  /** Opzioni del centro di costo, filtrate qui perche' il Combobox mostra le
   *  opzioni cosi' come le riceve. La voce vuota serve a togliere
   *  l'assegnazione, che con un Combobox non sarebbe altrimenti possibile. */
  const centriOptions: ComboboxOption[] = (() => {
    const q = centroFiltro.trim().toLowerCase();
    const trovati = centri
      .filter((c) => !q || `${c.codice} ${c.nome}`.toLowerCase().includes(q))
      .map((c) => ({ value: c.id, label: `${c.codice} - ${c.nome}` }));
    return fattura.centro_costo_id
      ? [{ value: '', label: 'Nessun centro di costo' }, ...trovati]
      : trovati;
  })();

  return (
    <div className="space-y-6 p-6">
      {/* Intestazione. `flex-wrap` era assente: con fino a otto bottoni la riga
          comprimeva il titolo o usciva dallo schermo su finestre strette.
          Aggiunta anche la DATA DEL DOCUMENTO, che il backend manda da sempre e
          che non era renderizzata da nessuna parte: per sapere di quando fosse
          una fattura si doveva aprire l'XML in fondo o tornare in lista. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          &larr; Indietro
        </Button>
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          Fattura {fattura.numero ?? fattura.id.slice(0, 8)}
        </h1>
        {fattura.data && (
          <span className="text-sm text-muted-foreground">
            del {new Date(fattura.data).toLocaleDateString('it-IT')}
          </span>
        )}
        <StatoBadge stato={fattura.stato} />
        <Badge variant="outline">{fattura.tipo_documento_codice}</Badge>
        <Badge variant={fattura.direzione === 'attiva' ? 'default' : 'secondary'}>
          {fattura.direzione === 'attiva' ? 'Attiva' : 'Passiva'}
        </Badge>
        <div className="ml-auto flex flex-wrap gap-2">
          <div className="flex items-center gap-2">
            {pdfErrore && (
              /* Era text-amber-600: 3,19:1 su bianco a 12px, sotto il minimo
                 AA di 4,5:1. Su un messaggio di errore non e' accettabile. */
              <span className="text-xs text-destructive">{pdfErrore}</span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleScaricaPdf}
              disabled={actionLoading}
            >
              {azione === 'pdf' ? 'Attendere...' : 'Scarica PDF'}
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={actionLoading || motivoBloccoPagamento !== null}
            onClick={() => {
              setPagamentoDaCorreggere(null);
              setShowPagamentoModal(true);
            }}
          >
            Registra pagamento
          </Button>
          {/* PRIMA azione su una bozza: la correzione. Fino al 2026-09-14 non
              esisteva affatto, e chi voleva correggere finiva sull'unico
              pulsante plausibile, che era la revoca. */}
          {fattura.stato === 'bozza' && fattura.direzione === 'attiva' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowModificaDialog(true)}
              disabled={actionLoading}
              title="Correzione rapida di data, controparte e P.IVA, senza lasciare questa pagina."
            >
              Modifica dati
            </Button>
          )}
          {/* Righe e importi si correggono nel form completo. Fino al
              2026-09-14 non erano modificabili in nessun modo: l'unica via era
              revocare la bozza e rifarla da zero. */}
          {fattura.stato === 'bozza' && fattura.direzione === 'attiva' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate(`/documentale/fatture/${fattura.id}/modifica?year=${year}`)
              }
              disabled={actionLoading}
              title="Apre la fattura per correggere righe, importi e dati di pagamento."
            >
              Modifica righe e importi
            </Button>
          )}
          {fattura.stato === 'bozza' && fattura.direzione === 'attiva' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleVerificaXml(false)}
              disabled={actionLoading}
              title="Controllo locale sullo schema ufficiale (XSD). Gratuito, usalo quanto vuoi."
            >
              {azione === 'verifica' ? 'Verifica...' : 'Verifica XML'}
            </Button>
          )}
          {fattura.stato === 'bozza' && fattura.direzione === 'attiva' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleVerificaXml(true)}
              disabled={actionLoading}
              title="Applica le regole reali del Sistema di Interscambio senza inviare. Consuma 1 credito Invoicetronic."
            >
              {azione === 'controllo' ? 'Controllo...' : 'Controllo SDI (1 credito)'}
            </Button>
          )}
          {fattura.stato === 'bozza' && fattura.direzione === 'attiva' && (
            <Button size="sm" onClick={handleInviaSdi} disabled={actionLoading}>
              {azione === 'invio' ? 'Invio...' : 'Invia SDI'}
            </Button>
          )}
          {/* La distruttiva NON e' adiacente alla primaria e non ha il
              riempimento pieno: prima un separatore, poi un bottone outline con
              il solo testo in destructive. "Invia SDI" e "Revoca bozza" a 8px
              di distanza, distinti solo dal colore di riempimento, sono la
              combinazione che ha prodotto la perdita del 2026-09-14. */}
          {fattura.stato === 'bozza' &&
            fattura.direzione === 'attiva' &&
            !fattura.stato_sdi && (
              <div className="flex items-center gap-2 pl-4 ml-2 border-l">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setShowRevocaDialog(true)}
                  disabled={actionLoading}
                >
                  {azione === 'revoca' ? 'Revoca in corso...' : 'Revoca bozza'}
                </Button>
              </div>
            )}
          {fattura.direzione === 'attiva' &&
            fattura.id_invoicetronic &&
            fattura.stato !== 'bozza' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleAggiornaStatoSdi}
                disabled={actionLoading}
              >
                {azione === 'aggiorna' ? 'Aggiorno...' : 'Aggiorna stato da SDI'}
              </Button>
            )}
          {STATI_RECUPERABILI.includes(fattura.stato) && fattura.direzione === 'attiva' && (
            <Button
              size="sm"
              variant="destructive"
              onClick={handleRiportaInBozza}
              disabled={actionLoading}
            >
              {azione === 'bozza' ? 'Attendere...' : 'Correggi e reinvia'}
            </Button>
          )}
          {/* Condizionato: prima era offerto SEMPRE, quindi anche su una bozza
              mai emessa e sulla nota di credito stessa. Una nota di credito
              storna un documento gia' emesso, e non si emette una nota di
              credito di una nota di credito. */}
          {STATI_EMESSA_VALIDA.includes(fattura.stato) &&
            fattura.fattura_riferimento_id === null && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  navigate(
                    `/documentale/fatture/nuova?direzione=${fattura.direzione}&nota_credito_di=${fattura.id}&year=${year}`
                  )
                }
              >
                Nota di credito
              </Button>
            )}
        </div>
      </div>

      {/* Errore di un'azione: banner sopra i dati, che restano a schermo.
          Prima questo stesso errore cancellava tutta la pagina (BL-054). */}
      {error && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-2 rounded border border-destructive/40 bg-destructive/5 p-3 text-sm"
        >
          <span className="text-destructive">{error}</span>
          <Button variant="ghost" size="sm" onClick={() => setError(null)}>
            Chiudi
          </Button>
        </div>
      )}

      {/* Confine del correggibile, dichiarato PRIMA che l'utente apra qualcosa.
          Senza questo, "Modifica dati" promette piu' di quello che mantiene e
          chi cerca gli importi finisce di nuovo sulla revoca. */}
      {fattura.stato === 'bozza' && fattura.direzione === 'attiva' && (
        <div className="rounded border bg-muted/40 p-3 text-sm">
          <strong>Bozza: non ancora emessa.</strong> Correzione rapida di data,
          controparte e P.IVA con &quot;Modifica dati&quot;, senza lasciare questa
          pagina. Per righe, importi e dati di pagamento usa &quot;Modifica righe e
          importi&quot;: i totali vengono ricalcolati dalle righe e il numero del
          documento non cambia. La revoca serve solo a buttare via la bozza, non a
          correggerla.
        </div>
      )}

      {/* Alert abbinamento */}
      {fattura.abbinamento_stato === 'in_attesa_manuale' && (
        <AbbinamentoAlert fatturaId={fattura.id} />
      )}

      {/* Dati documento */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Cedente / Prestatore</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p className="font-medium">{fattura.cedente_denominazione ?? '-'}</p>
            {fattura.cedente_piva && (
              <p className="text-muted-foreground">P.IVA {fattura.cedente_piva}</p>
            )}
            {fattura.cedente_cf && (
              <p className="text-muted-foreground">CF {fattura.cedente_cf}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Cessionario / Committente</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p className="font-medium">
              {fattura.cessionario_denominazione ?? '-'}
            </p>
            {fattura.cessionario_piva && (
              <p className="text-muted-foreground">
                P.IVA {fattura.cessionario_piva}
              </p>
            )}
            {fattura.cessionario_cf && (
              <p className="text-muted-foreground">CF {fattura.cessionario_cf}</p>
            )}
            {fattura.cessionario_codice_sdi && (
              <p className="text-muted-foreground">
                Codice SDI {fattura.cessionario_codice_sdi}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {fattura.origine_tipo && (
        <Card>
          <CardHeader>
            <CardTitle>Origine (catena documentale)</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-4 text-sm">
            {fattura.origine_tipo === 'ordine_vendita' && fattura.origine_id && (
              <button
                className="text-primary hover:underline"
                onClick={() => navigate(`/ordini-vendita/${fattura.origine_id}`)}
              >
                Ordine di vendita di origine
              </button>
            )}
            {fattura.ddt_id && (
              <button
                className="text-primary hover:underline"
                onClick={() => navigate(`/ddt/${fattura.ddt_id}`)}
              >
                DDT di origine
              </button>
            )}
            {fattura.documento_id && (
              <button
                className="text-primary hover:underline"
                onClick={() => navigate(`/documenti-commerciali/${fattura.documento_id}`)}
              >
                Documento di origine
              </button>
            )}
            {fattura.preventivo_id && (
              <button
                className="text-primary hover:underline"
                onClick={() => navigate(`/preventivi/${fattura.preventivo_id}`)}
              >
                Preventivo di origine
              </button>
            )}
          </CardContent>
        </Card>
      )}

      {fattura.acconti && fattura.acconti.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Acconti collegati (TD02)</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            {fattura.acconti.map((a) => (
              <button
                key={a.id}
                className="text-left text-primary hover:underline"
                onClick={() => navigate(`/documentale/fatture/${a.id}?year=${a.year}`)}
              >
                Fattura acconto n. {a.numero ?? a.id.slice(0, 8)}
                {a.importo_totale != null ? ` - ${a.importo_totale.toFixed(2)} EUR` : ''}
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Importi */}
      <Card>
        <CardHeader>
          <CardTitle>Importi</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Imponibile</p>
              <p className="font-mono text-lg font-medium">
                {fattura.importo_imponibile != null
                  ? fmtEUR.format(fattura.importo_imponibile)
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">IVA</p>
              <p className="font-mono text-lg font-medium">
                {fattura.importo_iva != null
                  ? fmtEUR.format(fattura.importo_iva)
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Totale</p>
              <p className="font-mono text-xl font-bold tabular-nums">
                {fattura.importo_totale != null
                  ? fmtEUR.format(fattura.importo_totale)
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Centro di costo</p>
              {/* Era un <select> nudo: con decine di centri si scorreva a mano.
                  Ora e' il Combobox di ui_core, con filtro durante la
                  digitazione. Il componente NON filtra da solo: riceve le
                  opzioni gia' scremate da centriOptions. */}
              <div className="mt-1">
                <Combobox
                  value={centroFiltro || centroSelezionatoLabel}
                  onInputChange={setCentroFiltro}
                  onSelect={(opt) => {
                    setCentroFiltro('');
                    void assegnaCentro(opt.value);
                  }}
                  options={centriOptions}
                  disabled={centroSalvataggio}
                  placeholder="Cerca un centro di costo..."
                />
              </div>
              {centroEsito && (
                <p
                  role="status"
                  className={
                    centroEsito.ok
                      ? 'mt-1 text-xs text-muted-foreground'
                      : 'mt-1 text-xs text-destructive'
                  }
                >
                  {centroEsito.testo}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Inversione contabile. La card si mostra da sola solo se il documento
          ha davvero righe senza imposta addebitata: su una fattura ordinaria
          non compare affatto, nemmeno disabilitata. Sta qui, fra gli importi e
          il pagamento, perche' e' informazione sul documento e sul suo obbligo,
          non un'azione di trasmissione da mettere in testata. */}
      {fattura.direzione === 'passiva' && (
        <IntegrazioneCard
          fatturaId={fattura.id}
          year={year}
          onIntegrata={() => void caricaFattura()}
          onVaiA={(altroId) => navigate(`/documentale/fatture/${altroId}?year=${year}`)}
        />
      )}

      {/* Pagamento - riepilogo, elenco dei pagamenti e azioni, tutto insieme.
          Prima erano due card separate da 317 righe di JSX e da sei sezioni che
          parlavano d'altro (ritenuta, bollo, note di credito, righe, allegati,
          esito SDI): per correggere un pagamento si attraversava mezza pagina.
          Due card contigue che descrivono lo stesso oggetto sono una card sola. */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle>Pagamento</CardTitle>
            <Button
              variant="outline"
              size="sm"
              disabled={actionLoading || motivoBloccoPagamento !== null}
              onClick={() => {
                setPagamentoDaCorreggere(null);
                setShowPagamentoModal(true);
              }}
            >
              Registra pagamento
            </Button>
          </div>
          {motivoBloccoPagamento && (
            /* Un bottone grigio che non dice perche' e' un vicolo cieco: il
               motivo sta accanto al bottone, in chiaro. Non in un tooltip,
               perche' un <button disabled> non emette eventi del mouse e il
               tooltip non comparirebbe mai. */
            <p className="mt-1 text-xs text-muted-foreground">{motivoBloccoPagamento}</p>
          )}
        </CardHeader>
        <CardContent>
          {/* Strato 1: i numeri che rispondono subito a "a che punto siamo". */}
          <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
            <div>
              <p className="text-muted-foreground">Totale documento</p>
              <p className="text-right font-mono font-medium tabular-nums">
                {fattura.importo_totale != null
                  ? fmtEUR.format(Number(fattura.importo_totale))
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Importo pagato</p>
              <p className="text-right font-mono font-medium tabular-nums">
                {fattura.importo_pagato != null
                  ? fmtEUR.format(Number(fattura.importo_pagato))
                  : '-'}
              </p>
            </div>
            <div>
              {/* Valore calcolato dal backend (BL-085): e' il NETTO atteso, cioe'
                  il totale meno la ritenuta, NON il residuo dopo gli acconti.
                  L'etichetta dice esattamente questo: chiamarlo "residuo"
                  sarebbe falso su una fattura con pagamenti parziali. */}
              <p className="text-muted-foreground">
                {fattura.direzione === 'attiva' ? 'Da incassare (netto)' : 'Da pagare (netto)'}
              </p>
              <p className="text-right font-mono font-medium tabular-nums">
                {fattura.importo_da_incassare != null
                  ? fmtEUR.format(Number(fattura.importo_da_incassare))
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Stato pagamento</p>
              <div className="mt-1">
                {fattura.stato_pagamento ? (
                  <StatoPagamentoBadge stato={fattura.stato_pagamento} />
                ) : (
                  <span>-</span>
                )}
              </div>
            </div>
          </div>

          <Separator className="my-4" />

          {/* Strato 2: i termini del pagamento. */}
          <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
            <div>
              <p className="text-muted-foreground">Scadenza</p>
              <p
                className={
                  fattura.data_scadenza &&
                  fattura.stato_pagamento !== 'pagata' &&
                  new Date(fattura.data_scadenza) < new Date()
                    ? 'font-medium text-destructive'
                    : 'font-medium'
                }
              >
                {fattura.data_scadenza
                  ? new Date(fattura.data_scadenza).toLocaleDateString('it-IT')
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Condizioni</p>
              <p className="font-medium">{fattura.condizioni_pagamento ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Modalita</p>
              <p className="font-medium">{fattura.modalita_pagamento ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">IBAN</p>
              <p className="font-mono font-medium">{fattura.iban_pagamento ?? '-'}</p>
            </div>
          </div>

          <Separator className="my-4" />

          {/* Strato 3: i pagamenti registrati, con le azioni accanto al dato su
              cui agiscono. La sezione si mostra SEMPRE, anche a elenco vuoto:
              e' cio' che rende distinguibile "nessun pagamento" da "non ho
              potuto leggerli". */}
          <p className="mb-2 text-sm font-medium">Pagamenti registrati</p>

          {pagamentiErrore && (
            <div
              role="alert"
              className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded border border-destructive/40 bg-destructive/5 p-3 text-sm"
            >
              <span className="text-destructive">{pagamentiErrore}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={pagamentiLoading}
                onClick={() => void caricaPagamenti()}
              >
                {pagamentiLoading ? 'Riprovo...' : 'Riprova'}
              </Button>
            </div>
          )}

          {pagamentiLoading && pagamenti.length === 0 && !pagamentiErrore && (
            <p className="py-2 text-sm text-muted-foreground">Caricamento dei pagamenti...</p>
          )}

          {!pagamentiLoading && !pagamentiErrore && pagamenti.length === 0 && (
            <p className="py-2 text-sm text-muted-foreground">
              Nessun pagamento registrato su questa fattura.
            </p>
          )}

          {pagamenti.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-right">Importo</TableHead>
                  <TableHead>Metodo</TableHead>
                  <TableHead>Riferimento</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagamenti.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      {p.data_pagamento
                        ? new Date(p.data_pagamento).toLocaleDateString('it-IT')
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {fmtEUR.format(Number(p.importo))}
                    </TableCell>
                    <TableCell>{p.modalita_codice ?? '-'}</TableCell>
                    <TableCell>{p.riferimento ?? '-'}</TableCell>
                    <TableCell className="text-right">
                      {/* Bersagli portati a 24px di altezza e separati di 16px:
                          sotto quella soglia un'azione benigna e una distruttiva
                          appiccicate si sbagliano (WCAG 2.2 SC 2.5.8). */}
                      <div className="flex items-center justify-end gap-4">
                        <button
                          type="button"
                          className="min-h-6 px-1 text-xs text-primary hover:underline disabled:opacity-50"
                          onClick={() => {
                            setPagamentoDaCorreggere(p);
                            setShowPagamentoModal(true);
                          }}
                          disabled={actionLoading}
                        >
                          Correggi
                        </button>
                        <button
                          type="button"
                          className="min-h-6 px-1 text-xs text-destructive hover:underline disabled:opacity-50"
                          onClick={() => void eliminaPagamento(p.id)}
                          disabled={actionLoading}
                        >
                          Elimina
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Ritenuta */}
      {fattura.importo_ritenuta != null && (
        <Card>
          <CardHeader>
            <CardTitle>Ritenuta d'acconto</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Tipo</p>
                <p className="font-medium">{fattura.tipo_ritenuta ?? '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Importo</p>
                <p className="font-mono font-medium">
                  {fmtEUR.format(fattura.importo_ritenuta)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Aliquota</p>
                <p className="font-medium">
                  {fattura.aliquota_ritenuta != null
                    ? `${fattura.aliquota_ritenuta}%`
                    : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bollo */}
      {fattura.importo_bollo != null && (
        <Card>
          <CardContent className="flex items-center gap-2 py-3 text-sm">
            <span className="text-muted-foreground">Bollo virtuale:</span>
            <span className="font-mono font-medium">
              {fmtEUR.format(fattura.importo_bollo)}
            </span>
          </CardContent>
        </Card>
      )}

      {/* Riferimento nota di credito */}
      {fattura.fattura_riferimento_id && (
        <Card>
          <CardContent className="py-3 text-sm">
            <Button
              variant="link"
              className="h-auto p-0"
              onClick={() =>
                navigate(
                  `/documentale/fatture/${fattura.fattura_riferimento_id}?year=${year}`
                )
              }
            >
              {/* L'etichetta si sceglie sul TIPO del documento corrente, non
                  sulla sola presenza del riferimento: lo stesso campo collega
                  sia le note di credito sia le integrazioni in inversione
                  contabile. Finche' diceva sempre "Nota di credito", su un
                  TD16 avrebbe dichiarato il falso. */}
              {TIPI_INTEGRAZIONE.includes(fattura.tipo_documento_codice)
                ? 'Integra la fattura di acquisto collegata'
                : 'Nota di credito della fattura collegata'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Righe */}
      {fattura.righe.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Righe ({fattura.righe.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead className="text-right">Qtà</TableHead>
                  <TableHead>U.M.</TableHead>
                  <TableHead className="text-right">Prezzo unit.</TableHead>
                  <TableHead className="text-right">IVA %</TableHead>
                  <TableHead>Natura</TableHead>
                  <TableHead className="text-right">Importo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fattura.righe.map((riga) => (
                  <TableRow key={riga.id}>
                    <TableCell className="text-muted-foreground">
                      {riga.numero_linea}
                    </TableCell>
                    <TableCell>{riga.descrizione ?? '-'}</TableCell>
                    <TableCell className="text-right font-mono">
                      {riga.quantita ?? '-'}
                    </TableCell>
                    <TableCell>{riga.unita_misura ?? ''}</TableCell>
                    <TableCell className="text-right font-mono">
                      {riga.prezzo_unitario != null
                        ? fmtEUR4.format(riga.prezzo_unitario)
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {riga.aliquota_iva_percentuale != null
                        ? `${riga.aliquota_iva_percentuale}%`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {riga.natura_iva_codice ? (
                        <Badge variant="outline">{riga.natura_iva_codice}</Badge>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right font-mono font-medium">
                      {riga.importo_riga != null
                        ? fmtEUR.format(riga.importo_riga)
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Allegati */}
      {fattura.allegati.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Allegati ({fattura.allegati.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {fattura.allegati.map((allegato) => (
                <li
                  key={allegato.id}
                  className="flex items-center justify-between rounded-md border p-3 text-sm"
                >
                  <span className="font-medium">{allegato.nome_file}</span>
                  <span className="text-muted-foreground">
                    {allegato.tipo_mime ?? ''}
                    {allegato.dimensione != null
                      ? ` - ${formatBytes(allegato.dimensione)}`
                      : ''}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Info SDI */}
      {fattura.id_invoicetronic && (
        <Card>
          <CardHeader>
            <CardTitle>Esito Sistema di Interscambio</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {fattura.stato === 'consegnata' && (
              <div className="rounded border border-green-300 bg-green-50 p-3 text-green-900 dark:bg-green-950/40 dark:text-green-200">
                <div className="font-medium">Consegnata al destinatario</div>
                <div>
                  Lo SDI ha recapitato il documento sul canale telematico del
                  destinatario ed e' fiscalmente valido. Nessuna azione richiesta.
                </div>
              </div>
            )}
            {/* Il caso che prima spariva dentro "Accettata": la fattura e' valida
                ma NON e' arrivata, e avvisare il cliente tocca a noi. */}
            {fattura.stato === 'non_consegnata' && (
              <div className="rounded border border-amber-400 bg-amber-50 p-3 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                <div className="font-medium">
                  Emessa e valida, ma NON consegnata al destinatario
                </div>
                <div>
                  Lo SDI non e' riuscito a recapitarla: il documento resta a
                  disposizione del cliente nella sua area riservata dell'Agenzia
                  delle Entrate. La fattura e' regolarmente emessa e non va rifatta
                  ne' rinviata. Devi pero' avvisare tu il cliente che la trova li',
                  e conviene mandargli la copia di cortesia con "Scarica PDF".
                </div>
              </div>
            )}
            {fattura.stato === 'trasmessa' && (
              <div className="rounded border border-neutral-300 bg-neutral-50 p-3 text-neutral-900 dark:bg-neutral-900/40 dark:text-neutral-200">
                <div className="font-medium">Trasmessa, esito non disponibile</div>
                <div>
                  Documento importato come gia' trasmesso: non abbiamo la ricevuta
                  dello SDI, quindi non sappiamo se sia stato consegnato.
                </div>
              </div>
            )}
            {fattura.stato === 'scartata' && (
              <div className="rounded border border-red-300 bg-red-50 p-3 text-red-900 dark:bg-red-950/40 dark:text-red-200">
                <div className="font-medium">Scartata dallo SDI</div>
                <div>
                  Il documento NON e' stato emesso. Correggi gli errori indicati e
                  reinvialo con "Correggi e reinvia": il numero resta lo stesso.
                </div>
              </div>
            )}
            {fattura.stato === 'accettata' && (
              <div className="rounded border border-green-300 bg-green-50 p-3 text-green-900 dark:bg-green-950/40 dark:text-green-200">
                <div className="font-medium">Accettata dalla Pubblica Amministrazione</div>
                <div>
                  L'ente destinatario ha accettato il documento. Questo esito esiste
                  solo per le fatture verso la PA.
                </div>
              </div>
            )}
            {fattura.stato === 'rifiutata' && (
              <div className="rounded border border-red-300 bg-red-50 p-3 text-red-900 dark:bg-red-950/40 dark:text-red-200">
                <div className="font-medium">Rifiutata dalla Pubblica Amministrazione</div>
                <div>
                  L'ente destinatario ha rifiutato il documento. Correggilo e
                  reinvialo con "Correggi e reinvia": il numero resta lo stesso.
                </div>
              </div>
            )}
            {fattura.stato === 'in_invio' && (
              <div className="rounded border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                <div className="font-medium">In attesa dell'esito</div>
                <div>
                  Il documento e' stato trasmesso. Lo SDI risponde di norma entro
                  poche ore, per legge entro cinque giorni. L'esito arriva da solo;
                  con "Aggiorna stato da SDI" puoi comunque forzare il controllo.
                </div>
              </div>
            )}
            {fattura.sdi_errori && fattura.sdi_errori.length > 0 && (
              <div>
                <div className="mb-1 font-medium">Motivo dello scarto</div>
                <ul className="list-disc pl-5 text-red-700 dark:text-red-300">
                  {fattura.sdi_errori.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}
            {fattura.sdi_descrizione && (
              <p>
                <span className="text-muted-foreground">Notifica SDI:</span>{' '}
                {fattura.sdi_descrizione}
              </p>
            )}
            {fattura.data_ricezione_sdi && (
              <p>
                <span className="text-muted-foreground">Ultimo aggiornamento:</span>{' '}
                {new Date(fattura.data_ricezione_sdi).toLocaleString('it-IT')}
              </p>
            )}
            {fattura.stato_sdi && (
              <p>
                <span className="text-muted-foreground">Stato SDI:</span>{' '}
                <Badge variant="outline">{fattura.stato_sdi}</Badge>
              </p>
            )}
            <p>
              <span className="text-muted-foreground">ID Invoicetronic:</span>{' '}
              <span className="font-mono">{fattura.id_invoicetronic}</span>
            </p>
          </CardContent>
        </Card>
      )}

      {/* La card "Pagamenti registrati" che stava qui e' confluita nella card
          Pagamento, molto piu' in alto: era separata dal riepilogo che descrive
          dallo stesso oggetto da sei sezioni che parlano d'altro. */}

      {/* XML Fattura Elettronica - foglio di stile ufficiale AdE */}
      {fattura.has_xml && (
        <AdeXmlViewer
          fatturaId={fattura.id}
          year={Number(year)}
          tipoDocumento={fattura.tipo_documento_codice}
        />
      )}

      {showVerifica && verificaXml && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowVerifica(false)}
        >
          <div
            className="max-h-[85vh] w-full max-w-3xl overflow-auto rounded-lg bg-white p-6 shadow-xl dark:bg-neutral-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-outfit text-xl font-semibold">Verifica XML pre-invio SDI</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowVerifica(false)}>
                Chiudi
              </Button>
            </div>
            {!verificaXml.valido ? (
              <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-300">
                XML NON conforme: correggi gli errori sotto prima di inviare a SDI.
              </div>
            ) : verificaXml.sdi_eseguito ? (
              <div className="mb-4 rounded border border-green-300 bg-green-50 p-3 text-sm text-green-800 dark:bg-green-950/40 dark:text-green-300">
                XML valido: passa i controlli locali (XSD) e la pre-validazione
                Invoicetronic (regole SDI). Nota: l'esito definitivo lo da' SDI con la
                notifica dopo l'invio; alcuni controlli su registri esterni non sono
                verificabili prima.
              </div>
            ) : (
              <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                <div>
                  Struttura corretta: l'XML passa i controlli locali sullo schema
                  ufficiale (XSD). Le regole del Sistema di Interscambio NON sono
                  ancora state controllate.
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => handleVerificaXml(true)}
                  disabled={actionLoading}
                >
                  {azione === 'controllo' ? 'Controllo...' : 'Esegui controllo SDI (1 credito)'}
                </Button>
              </div>
            )}
            {verificaXml.errori_xsd.length > 0 && (
              <div className="mb-3">
                <div className="mb-1 font-medium">Errori schema (XSD)</div>
                <ul className="list-disc pl-5 text-sm text-red-700 dark:text-red-300">
                  {verificaXml.errori_xsd.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}
            {verificaXml.errori_sdi.length > 0 && (
              <div className="mb-3">
                <div className="mb-1 font-medium">Controlli SDI (Invoicetronic)</div>
                <ul className="list-disc pl-5 text-sm text-amber-700 dark:text-amber-300">
                  {verificaXml.errori_sdi.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mb-1 font-medium">Anteprima XML</div>
            <pre className="max-h-72 overflow-auto rounded bg-neutral-100 p-3 text-xs dark:bg-neutral-800">
              {verificaXml.xml}
            </pre>
          </div>
        </div>
      )}

      {showPagamentoModal && (
        <PagamentoFatturaModal
          fatturaId={fattura.id}
          year={Number(year)}
          // Residuo calcolato in centesimi interi: la sottrazione in virgola
          // mobile precompilava il campo importo, cioe' un dato fiscale.
          residuo={
            (Math.round((fattura.importo_totale ?? 0) * 100) -
              Math.round(Number(fattura.importo_pagato ?? 0) * 100)) /
            100
          }
          // Ritenuta d'acconto: il modal propone in automatico la quota
          // operata con questo pagamento, proporzionale all'importo.
          ritenutaDocumento={Number(fattura.importo_ritenuta ?? 0) || null}
          ritenutaGiaOperata={pagamenti.reduce(
            (somma, p) => somma + Number(p.ritenuta_operata ?? 0),
            0,
          )}
          nettoAtteso={Number(fattura.importo_da_incassare ?? 0) || null}
          pagamento={pagamentoDaCorreggere}
          onClose={() => {
            setShowPagamentoModal(false);
            setPagamentoDaCorreggere(null);
          }}
          onSaved={() => {
            setShowPagamentoModal(false);
            setPagamentoDaCorreggere(null);
            void caricaFattura();
            void caricaPagamenti();
          }}
        />
      )}

      {fattura && (
        <ModificaAnagraficaFatturaDialog
          open={showModificaDialog}
          onOpenChange={setShowModificaDialog}
          fatturaId={fattura.id}
          year={year}
          valori={{
            data: fattura.data,
            cedente_denominazione: fattura.cedente_denominazione,
            cedente_piva: fattura.cedente_piva,
            cessionario_denominazione: fattura.cessionario_denominazione,
            cessionario_piva: fattura.cessionario_piva,
          }}
          soloLettura={{
            cessionario_codice_sdi: fattura.cessionario_codice_sdi,
            cessionario_cf: fattura.cessionario_cf,
            importo_totale: fattura.importo_totale,
          }}
          authHeaders={auth}
          onSaved={(salvati) => {
            // Merge dei soli campi salvati: la risposta del PATCH ha uno schema
            // piu' snello del GET dettaglio (niente righe, niente pagamenti) e
            // sostituire la fattura con quella farebbe sparire mezza pagina.
            setFattura((prev) => (prev ? { ...prev, ...salvati } : prev));
            toast.success('Dati della fattura aggiornati.');
          }}
        />
      )}

      {fattura && (
        <RevocaBozzaDialog
          open={showRevocaDialog}
          onOpenChange={setShowRevocaDialog}
          numero={fattura.numero}
          daOrdineVendita={
            fattura.origine_tipo === 'ordine_vendita' && !!fattura.origine_id
          }
          inCorso={azione === 'revoca'}
          onConferma={() => void handleRevoca()}
        />
      )}
    </div>
  );
};

export default FatturaDetail;
